import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { scrollToSection } from "./Navbar";

const DEFAULT_IMAGE = "https://images.unsplash.com/photo-1704325053034-36e7f7f02223?crop=entropy&cs=srgb&fm=jpg&q=85";
const DEFAULT_LINES = ["PRECISION.", "POWER.", "WERKHAUS."];
const DEFAULT_EYEBROW = "EST. 2026 /// GERMAN VEHICLE SPECIALISTS";
const DEFAULT_SUBTEXT = "Factory-grade diagnostics, performance tuning and meticulous care for Porsche, BMW, Mercedes-AMG and Audi — every bolt treated like it matters.";
const EASE = [0.22, 1, 0.36, 1];

export default function Hero({ onBook, content }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const image = content?.hero_image || DEFAULT_IMAGE;
  const lines = content?.hero_lines?.length ? content.hero_lines : DEFAULT_LINES;
  const eyebrow = content?.est_label || DEFAULT_EYEBROW;
  const subtext = content?.hero_subtext || DEFAULT_SUBTEXT;

  return (
    <section ref={ref} className="relative min-h-screen flex items-end overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        <motion.img
          src={image}
          alt="Performance car in dark studio"
          className="w-full h-full object-cover"
          initial={{ scale: 1.15 }}
          animate={{ scale: 1.02 }}
          transition={{ duration: 2.4, ease: EASE }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0D12] via-[#0A0D12]/55 to-[#0A0D12]/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0D12]/80 via-transparent to-transparent" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pb-24 pt-40 w-full">
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.7, ease: EASE }}
          className="font-mono text-xs uppercase tracking-[0.35em] text-crimson font-semibold mb-6"
          data-testid="hero-eyebrow"
        >
          {eyebrow}
        </motion.p>

        <h1 className="font-display font-extrabold uppercase leading-[0.92] tracking-tight text-6xl sm:text-7xl lg:text-8xl xl:text-9xl">
          {lines.map((line, i) => (
            <span key={`${line}-${i}`} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${i === lines.length - 1 ? "text-crimson" : "text-slate-100"}`}
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ delay: 0.3 + i * 0.14, duration: 0.9, ease: EASE }}
                data-testid={`hero-line-${i}`}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.7, ease: EASE }}
          className="mt-6 max-w-xl text-base sm:text-lg text-slate-300 leading-relaxed"
          data-testid="hero-subtext"
        >
          {subtext}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.7, ease: EASE }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-booking-cta-button"
            onClick={onBook}
            className="bg-crimson text-white font-display font-semibold uppercase tracking-widest px-8 py-4 text-base hover:-translate-y-0.5 transition-transform hover:shadow-[0_0_32px_0_rgba(255,56,21,0.45)]"
          >
            Book a Service
          </button>
          <button
            data-testid="hero-inventory-cta-button"
            onClick={() => scrollToSection("inventory")}
            className="border border-slate-500 text-slate-100 font-display font-semibold uppercase tracking-widest px-8 py-4 text-base hover:border-crimson hover:text-crimson transition-colors"
          >
            View Inventory
          </button>
        </motion.div>
      </motion.div>

      <motion.button
        onClick={() => scrollToSection("services")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        className="absolute bottom-6 right-6 sm:right-10 z-10 text-slate-400 hover:text-crimson transition-colors"
        aria-label="Scroll down"
        data-testid="hero-scroll-indicator"
      >
        <ChevronDown size={28} className="animate-bounce" />
      </motion.button>
    </section>
  );
}
